import Header from '@/components/Header';
import Toast from '@/components/Toast';
import { useRouter } from 'next/router';
import useNetworkConnection from '@/hooks/useNetworkConnection';
import { SOCKET } from '@/hooks/useWebSocket';
import useSetting from '@/queries/useSetting';
import useStoreApp from '@/store';
import { SOCKET_CONFIG } from '@/types/consistency';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { ReactNode, memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Theme, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Footer } from '../Footer';

const footerHeightXs = '720px';
const footerHeightSm = '480px';
const footerHeightMd = '400px';

interface IProps {
  hasBackground?: boolean;
  hasFooter?: boolean;
  children?: ReactNode;
  backgroundColor?: string;
  headerBackgroundColor?: string;
  showTabs?: boolean;
  hideHeader?: boolean;
  hasPadding?: boolean;
}

function Layout({
  children,
  hasFooter = false,
  hasBackground = false,
  backgroundColor,
  headerBackgroundColor,
  showTabs = false,
  hideHeader = false,
  hasPadding = true,
}: IProps) {
  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);
  const styleBackground = {
    backgroundImage: {
      xs: 'url("/assets/images/mobileBackground.png")',
      sm: 'url("/assets/images/background.webp")',
    },
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center center',
    backgroundAttachment: 'fixed',
  };

  const theme = useTheme();
  const router = useRouter();
  const { t } = useTranslation('common');
  const { setSocketBookingMessage } = useStoreApp(state => state.globalSlice);

  const isFromHagoCom = router.query.from === 'hoiango.com';

  const useScrollableTransparentHeader = typeof headerBackgroundColor !== 'undefined';
  let haveHeader = !hideHeader;
  let haveFooter = hasFooter;

  if (isFromHagoCom) {
    haveHeader = false;
    haveFooter = false;
  }

  useSetting();

  const { isOnline, changed, setChanged } = useNetworkConnection();

  useEffect(() => {
    SOCKET.on(SOCKET_CONFIG.HAS_CHANGE_ORDER_INSIDE, message => {
      setSocketBookingMessage(message);
    });

    return () => {
      SOCKET.off(SOCKET_CONFIG.HAS_CHANGE_ORDER_INSIDE);
    };
  }, []);

  useEffect(() => {
    if (!useScrollableTransparentHeader) {
      setIsHeaderScrolled(false);
      return;
    }

    const handleScroll = () => {
      setIsHeaderScrolled(window.scrollY > 4);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [useScrollableTransparentHeader]);

  return (
    <>
      {haveHeader && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.appBar + 10,
            height: '72px',
            boxShadow: useScrollableTransparentHeader
              ? isHeaderScrolled
                ? hasBackground
                  ? 'none'
                  : '0px 4px 20px rgba(13, 110, 253, 0.1)'
                : 'none'
              : hasBackground
                ? 'none'
                : '0px 4px 20px rgba(13, 110, 253, 0.1)',
            borderBottom: 'none',
            backgroundColor: useScrollableTransparentHeader
              ? isHeaderScrolled
                ? alpha(headerBackgroundColor ?? theme.palette.blue.main, 0.8)
                : headerBackgroundColor
              : theme.palette.blue.main,
            backdropFilter:
              useScrollableTransparentHeader && isHeaderScrolled
                ? 'blur(8px)'
                : 'none',
            WebkitBackdropFilter:
              useScrollableTransparentHeader && isHeaderScrolled
                ? 'blur(8px)'
                : 'none',
            transition: 'background-color 220ms ease, backdrop-filter 220ms ease, box-shadow 220ms ease',
          }}
        >
          <Header showTabs={showTabs} />
        </Box>
      )}

      <Box
        sx={
          hasBackground
            ? {
              position: 'relative',
              minHeight: '100dvh',
              ...styleBackground,
            }
            : {
              position: 'relative',
              minHeight: '100dvh',
              backgroundColor: backgroundColor ?? theme.palette.white.main,
              paddingLeft: {
                xs: '0px',
                sm: hasPadding ? '0px' : '0px !important'
              }, // test for e-taxi tab -> has padding old is 16px
              paddingRight: {
                xs: '0px',
                sm: hasPadding ? '0px' : '0px !important'
              }, // test for e-taxi tab -> has padding old is 16px
            }
        }
      >
        <Container maxWidth={hasPadding ? "lg" : false}
          sx={
            {
              paddingLeft: {
                xs: 0,
                sm: hasPadding ? '24px' : '0px !important',
              },
              paddingRight: {
                xs: 0,
                sm: hasPadding ? '24px' : '0px !important'
              }
            }
          }
        >
          {/* Header space */}
          {haveHeader ? <HeaderSpace /> : null}
          {children}
          {/* Footer space */}
          {haveFooter && <FooterSpace hasBackground={hasBackground} />}
        </Container>

        {haveFooter ? <Footer /> : null}
      </Box>

      <Toast
        open={changed}
        onClose={() => {
          setChanged(false);
        }}
        severity={isOnline ? 'success' : 'error'}
        message={isOnline ? t('networkYES') : t('networkNO')}
      />
      <ToastContainer position="top-right" autoClose={4000} />
    </>
  );
}

export default memo(Layout);


// Header space
const HeaderSpace = styled(Box)({
  height: '72px',
});

// Footer space
const FooterSpace = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hasBackground',
})<{ hasBackground?: boolean }>(({ theme, hasBackground }) =>
  hasBackground
    ? {
      height: footerHeightXs, // xs
      [theme.breakpoints.up('sm')]: {
        height: footerHeightSm,
      },
      [theme.breakpoints.up('md')]: {
        height: footerHeightMd,
      },
    }
    : {
      height: footerHeightXs, // xs
      [theme.breakpoints.up('sm')]: {
        height: footerHeightSm,
      },
      [theme.breakpoints.up('md')]: {
        height: footerHeightMd,
      },
    }
);
